import { gql, useMutation, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import {
  GenerateTicketDistributionLinkMutation,
  GenerateTicketDistributionLinkMutationVariables,
  RegenerateTicketDistributionLink,
  RegenerateTicketDistributionLinkMutation,
  RegenerateTicketDistributionLinkMutationVariables,
  TicketShareSelectQuery,
  TicketShareSelectQueryVariables,
  TicketShareSelectUserItemSerialFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { useInitialLoading } from '@/components/lib/initialLoading'
import { useMessage, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, generateQueryPath, routerPages } = useRouter<{
    itemId: string
    serialId: string
    throughTicketIndex?: string
  }>()
  const { showError } = useMessage()

  const { data, loading } = useQuery<
    TicketShareSelectQuery,
    TicketShareSelectQueryVariables
  >(QUERY, {
    variables: { id: router.query.itemId },
  })

  const [generateTicketDistributionLink, { loading: generating }] = useMutation<
    GenerateTicketDistributionLinkMutation,
    GenerateTicketDistributionLinkMutationVariables
  >(GENERATE_TICKET_DISTRIBUTION_LINK)

  const [regenerateTicketDistributionLink, { loading: regenerating }] =
    useMutation<
      RegenerateTicketDistributionLinkMutation,
      RegenerateTicketDistributionLinkMutationVariables
    >(RegenerateTicketDistributionLink)

  const serial = useMemo(
    () =>
      data?.portfolio?.serials?.find(
        serial => serial?.serial?.id === router.query.serialId
      ) ?? null,
    [data?.portfolio, router.query.serialId]
  )

  return {
    state: {
      loading: useInitialLoading(data, loading),
      generating,
      regenerating,
      serial,
      throughTicketIndex: data?.portfolio?.item.metadata?.ticket
        ?.isThroughTicket
        ? router.query.throughTicketIndex
        : undefined,
      assignedNumberType:
        data?.portfolio?.item?.metadata?.ticket?.assignedNumberType ?? null,
      artistId: data?.portfolio?.item?.issuedArtist?.id ?? '',
    },
    actions: {
      async onGenerateTicketDistribution() {
        await generateTicketDistributionLink({
          variables: {
            inputs: {
              itemId: router.query.itemId,
              serialId: router.query.serialId,
            },
          },
        })
          .then(() => {
            router.replace(
              generateQueryPath(
                routerPages.TicketShareCopy,
                {
                  itemId: router.query.itemId,
                  serialId: router.query.serialId,
                },
                {
                  throughTicketIndex: router.query.throughTicketIndex ?? '',
                  isFirstGenerate: 'true',
                }
              )
            )
          })
          .catch(showError)
      },
      async onRegenerateTicketDistribution() {
        await regenerateTicketDistributionLink({
          variables: {
            inputs: {
              token: serial?.serial?.ticketDistributionLink?.token ?? '',
            },
          },
        })
          .then(() => {
            router.replace(
              generateQueryPath(
                routerPages.TicketShareCopy,
                {
                  itemId: router.query.itemId,
                  serialId: router.query.serialId,
                },
                { throughTicketIndex: router.query.throughTicketIndex ?? '' }
              )
            )
          })
          .catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query TicketShareSelect($id: ID!) {
    portfolio(id: $id) {
      item {
        metadata {
          ticket {
            assignedNumberType
            isThroughTicket
          }
        }
        issuedArtist {
          id
        }
      }
      serials {
        ...TicketShareSelectUserItemSerial
      }
    }
  }
  ${TicketShareSelectUserItemSerialFragmentDoc}
`

const GENERATE_TICKET_DISTRIBUTION_LINK = gql`
  mutation GenerateTicketDistributionLink(
    $inputs: GenerateTicketDistributionLinkInput!
  ) {
    generateTicketDistributionLink(inputs: $inputs) {
      id
    }
  }
`
